import { useState, useEffect, useCallback, useRef, type Dispatch, type SetStateAction } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Sidebar } from '@/components/brand/site-mapper/components/Sidebar';
import { Canvas } from '@/components/brand/site-mapper/components/Canvas';
import { FloatingHeader } from '@/components/brand/site-mapper/components/FloatingHeader';
import { SectionEditorSidebar } from '@/components/brand/site-mapper/components/SectionEditorSidebar';
import { PageEditorSidebar } from '@/components/brand/site-mapper/components/PageEditorSidebar';
import { CreateSitemapDialog } from '@/components/brand/site-mapper/components/CreateSitemapDialog';
import { useSitemap } from '@/hooks/useSitemap';
import { useWorkspace } from '@/context/WorkspaceContext';

interface SitemapEditorProps {
  isGlobalDarkMode: boolean;
  setIsGlobalDarkMode: (dark: boolean) => void;
}

export interface SitemapEditorContentProps extends SitemapEditorProps {
  sitemapId: string;
  activeMode: 'sitemap' | 'comment' | 'design-system';
  setActiveMode: (mode: 'sitemap' | 'comment' | 'design-system') => void;
  sidebarOpen: boolean;
  setSidebarOpen: Dispatch<SetStateAction<boolean>>;
}

export const SitemapEditorContent = ({ 
  isGlobalDarkMode, 
  setIsGlobalDarkMode,
  sitemapId,
  activeMode,
  setActiveMode,
  sidebarOpen,
  setSidebarOpen
}: SitemapEditorContentProps) => {
  const { currentWorkspace } = useWorkspace();
  const workspaceAccentColor = currentWorkspace?.appearance?.theme?.accentColor || currentWorkspace?.color || '#3B82F6';
  const sitemap = useSitemap(sitemapId, workspaceAccentColor);
  const [selectedPageIds, setSelectedPageIds] = useState<string[]>([]);
  const [editingSection, setEditingSection] = useState<{ pageId: string; sectionId: string } | null>(null);
  const [editingPageId, setEditingPageId] = useState<string | null>(null);
  const [sidebarInitialTab, setSidebarInitialTab] = useState<'editor' | 'files' | 'comments'>('editor');
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isNew = searchParams.get('new') === 'true';

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const hasShownEmptyDialog = useRef(false);

  useEffect(() => {
    if (!sitemap.isLoading && isNew && !hasShownEmptyDialog.current) {
      setShowCreateDialog(true);
      hasShownEmptyDialog.current = true;
    }
  }, [sitemap.isLoading, isNew]);

  const handleSelectPage = useCallback((id: string | null, additive?: boolean) => {
    if (!id) {
      setSelectedPageIds([]);
      return;
    }
    if (additive) {
      setSelectedPageIds(prev =>
        prev.includes(id) ? prev.filter(pid => pid !== id) : [...prev, id]
      );
    } else {
      setSelectedPageIds([id]);
    }
  }, []);

  const handleSelectPages = useCallback((ids: string[]) => {
    setSelectedPageIds(ids);
  }, []);

  const handleAddSectionToPage = useCallback((sectionName: string) => {
    if (selectedPageIds.length === 1) {
      sitemap.addSection(selectedPageIds[0], sectionName);
    }
  }, [selectedPageIds, sitemap.addSection]);

  const handleEditSection = useCallback((pageId: string, sectionId: string) => {
    setEditingSection({ pageId, sectionId });
    setEditingPageId(null);
    setSidebarInitialTab('editor');
  }, []);

  const handleEditPage = useCallback((pageId: string) => {
    setEditingPageId(pageId);
    setEditingSection(null);
    setSidebarInitialTab('editor');
  }, []);

  const handleCommentSection = useCallback((pageId: string, sectionId: string) => {
    setEditingSection({ pageId, sectionId });
    setEditingPageId(null);
    setSidebarInitialTab('comments');
  }, []);

  const handleCommentPage = useCallback((pageId: string) => {
    setEditingPageId(pageId);
    setEditingSection(null);
    setSidebarInitialTab('comments');
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        sitemap.save();
        return;
      }
      if (e.key === 'Escape') {
        if (editingSection) {
          setEditingSection(null);
          return;
        }
        if (editingPageId) {
          setEditingPageId(null);
          return;
        }
        sitemap.cancelConnection();
        setSelectedPageIds([]);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [sitemap.cancelConnection, sitemap.save, editingSection, editingPageId]);

  // Find editing page and section
  const editingPage = editingSection ? sitemap.pages.find(p => p.id === editingSection.pageId) : null;
  const editingSectionData = editingPage ? editingPage.sections.find(s => s.id === editingSection?.sectionId) : null;

  return (
    <div className={`flex h-screen overflow-hidden ${isGlobalDarkMode ? 'bg-background' : 'bg-transparent'} text-foreground relative`}>
      {/* Sidebar with transition */}
      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden ${
          sidebarOpen ? 'w-72 min-w-[18rem]' : 'w-0 min-w-0'
        }`}
      >
        <Sidebar
          blueprintName={sitemap.sitemapName}
          onAddPage={sitemap.addPage}
          onAddSectionToPage={handleAddSectionToPage}
          pageCount={sitemap.pages.length}
          selectedPageId={selectedPageIds.length === 1 ? selectedPageIds[0] : null}
          onToggleSidebar={() => setSidebarOpen(false)}
          backPath={location.pathname.includes('/design-studio/') || location.pathname.includes('/blueprints/') ? '/brand/blueprints' : '/brand/sitemaps'}
        />
      </div>

      {/* Canvas area */}
      <Canvas
        pages={sitemap.pages}
        connections={sitemap.connections}
        selectedPageIds={selectedPageIds}
        onSelectPage={handleSelectPage}
        onSelectPages={handleSelectPages}
        onBatchPositionChange={sitemap.batchUpdatePositions}
        onDelete={sitemap.deletePage}
        onDuplicate={sitemap.duplicatePage}
        onRemoveSection={sitemap.removeSection}
        onDuplicateSection={sitemap.duplicateSection}
        onMoveSectionToPage={sitemap.moveSectionToPage}
        onReorderSections={sitemap.reorderSections}
        onGenerateAllCopy={sitemap.generateAllCopy}
        onStartConnection={sitemap.startConnection}
        onCompleteConnection={sitemap.completeConnection}
        onRemoveConnection={sitemap.removeConnection}
        onUpdateConnectionStyle={sitemap.updateConnectionStyle}
        onCancelConnection={sitemap.cancelConnection}
        onChangeColor={sitemap.updatePageColor}
        onChangeSectionColor={sitemap.updateSectionColor}
        onEditSection={handleEditSection}
        onEditPage={handleEditPage}
        onCommentSection={handleCommentSection}
        onCommentPage={handleCommentPage}
        activeMode={activeMode === 'design-system' ? 'sitemap' : activeMode}
        onAddPage={sitemap.addPage}
        onAddSectionToPage={handleAddSectionToPage}
        onAddSectionToPageById={sitemap.addSection}
        onAddPageRelative={sitemap.addPageRelative}
        onSwapPagePositions={sitemap.swapPagePositions}
        connectingFrom={sitemap.connectingFrom}
        editingSectionId={editingSection?.sectionId ?? null}
        headerContent={
          <FloatingHeader
            sidebarOpen={sidebarOpen}
            onToggleSidebar={() => setSidebarOpen(prev => !prev)}
            activeMode={activeMode}
            onChangeMode={(mode) => {
              setActiveMode(mode);
              setSidebarInitialTab(mode === 'comment' ? 'comments' : 'editor');
            }}
            isGlobalDarkMode={isGlobalDarkMode}
            setIsGlobalDarkMode={setIsGlobalDarkMode}
            isSaving={sitemapId ? sitemap.isSaving : undefined}
            lastSaved={sitemapId ? sitemap.lastSaved : null}
            backPath={location.pathname.includes('/design-studio/') || location.pathname.includes('/blueprints/') ? '/brand/blueprints' : '/brand/sitemaps'}
            onSave={() => sitemap.save()}
          />
        }
      />

      {/* Section Editor Sidebar */}
      {editingPage && editingSectionData && (
        <SectionEditorSidebar
          page={editingPage}
          section={editingSectionData}
          onUpdateCopy={sitemap.updateSectionCopy}
          onGenerateCopy={sitemap.generateCopy}
          onClose={() => setEditingSection(null)}
          onNavigateToPage={handleEditPage}
          initialTab={sidebarInitialTab}
          onTabChange={(tab: any) => {
            setSidebarInitialTab(tab);
            setActiveMode(tab === 'comments' ? 'comment' : 'sitemap');
          }}
        />
      )}

      {/* Page Editor Sidebar */}
      {editingPageId && (() => {
        const pageData = sitemap.pages.find(p => p.id === editingPageId);
        if (!pageData) return null;
        return (
          <PageEditorSidebar
            page={pageData}
            onUpdateMeta={sitemap.updatePageMeta}
            onClose={() => setEditingPageId(null)}
            initialTab={sidebarInitialTab}
            onTabChange={(tab: any) => {
              setSidebarInitialTab(tab);
              setActiveMode(tab === 'comments' ? 'comment' : 'sitemap');
            }}
            isGlobalDarkMode={isGlobalDarkMode}
          />
        );
      })()}
      <CreateSitemapDialog
        show={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onCreateScratch={() => setShowCreateDialog(false)}
      />
    </div>
  );
};

export const SitemapEditor = ({ isGlobalDarkMode, setIsGlobalDarkMode }: SitemapEditorProps) => {
  const { id: sitemapId } = useParams<{ id: string }>();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeMode, setActiveMode] = useState<'sitemap' | 'comment' | 'design-system'>('sitemap');

  if (!sitemapId) return null;

  return (
    <SitemapEditorContent
      isGlobalDarkMode={isGlobalDarkMode}
      setIsGlobalDarkMode={setIsGlobalDarkMode}
      sitemapId={sitemapId}
      activeMode={activeMode}
      setActiveMode={setActiveMode}
      sidebarOpen={sidebarOpen}
      setSidebarOpen={setSidebarOpen}
    />
  );
};

export default SitemapEditor;
